import { formatValue } from "./format";
import { Field, fieldLabel, isSecretField } from "./schema-field";

export const EXPORT_ROW_CAP = 5000;
export const EXPORT_PAGE_SIZE = 100;

const SKIP_COLUMNS = new Set(["actions", "__check__"]);

export const escapeCsv = (value: string): string =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

export const visibleExportFields = (
  schema: Field[],
  columnVisibility: { [key: string]: boolean },
  columnOrder: string[]
): Field[] => {
  // The export reads the schema, not the rendered columns, so hiding a secret
  // column in the grid would not have hidden it from the CSV. Same rule here.
  const exportable = (name: string) =>
    !SKIP_COLUMNS.has(name) &&
    !name.startsWith("_") &&
    !isSecretField(name) &&
    columnVisibility[name] !== false;

  const byName = new Map(schema.map((field) => [field.name, field]));
  const seen = new Set<string>();
  const fields: Field[] = [];

  for (const name of columnOrder) {
    const field = byName.get(name);
    if (!field || !exportable(name)) continue;
    fields.push(field);
    seen.add(name);
  }
  for (const field of schema) {
    if (seen.has(field.name) || !exportable(field.name)) continue;
    fields.push(field);
  }
  return fields;
};

export const rowsMatchingSelection = (
  rows: any[],
  selectedIds: ReadonlyArray<string | number>,
  idField?: string
): any[] => {
  const idSet = new Set(selectedIds.map(String));
  return rows.filter((row) => {
    if (idField && row[idField] != null && idSet.has(String(row[idField]))) {
      return true;
    }
    if (row.id != null && idSet.has(String(row.id))) return true;
    return false;
  });
};

export const csvLinesFromRows = (rows: any[], fields: Field[]): string[] => {
  const header = fields.map((field) => escapeCsv(fieldLabel(field))).join(",");
  const body = rows.map((row) =>
    fields
      .map((field) => escapeCsv(formatValue(row[field.name], field)))
      .join(",")
  );
  return [header, ...body];
};

export const downloadCsv = (filename: string, lines: string[]): void => {
  const blob = new Blob(["\uFEFF" + lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
